import ExcelJS from 'exceljs';
import { format } from 'date-fns';

/**
 * Generates flags text for Excel export
 */
const generateFlagsText = (job) => {
  const flags = [];
  if (job.isEarlyCheckin) flags.push('Early Check-in');
  if (job.isLateCheckout) flags.push('Late Checkout');
  if (job.isOwnerBlock) flags.push('Owner Block');
  if (job.isMaintenanceBlock) flags.push('Maintenance');
  return flags.length > 0 ? flags.join(', ') : '';
};

const formatTeamMemberName = (teamMember) => {
  if (!teamMember) return 'Unassigned';
  const baseName = teamMember.teamMemberName || 'Unassigned';
  return teamMember.usesSalaryCalculation ? `${baseName} (Salary)` : baseName;
};

const applyTotalsRowStyle = (row) => {
  if (!row) return;
  row.eachCell(cell => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF3F4F6' }
    };
    const existingFont = cell.font || {};
    cell.font = { ...existingFont, bold: true };
  });
};

const applyHeaderRowStyle = (row) => {
  if (!row) return;
  row.eachCell(cell => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFCBD5F5' }
    };
    const existingFont = cell.font || {};
    cell.font = { ...existingFont, bold: true, size: 12 };
    const existingAlignment = cell.alignment || {};
    cell.alignment = { ...existingAlignment, vertical: 'middle' };
    const existingBorder = cell.border || {};
    cell.border = {
      ...existingBorder,
      bottom: { style: 'thin', color: { argb: 'FF94A3B8' } }
    };
  });
};

const toCurrencyNumber = (value) => {
  const numericValue = Number(value || 0);
  if (Number.isNaN(numericValue)) return 0;
  return Number(numericValue.toFixed(2));
};

const formatCurrencyDisplay = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
}).format(Number(value || 0));

const formatCurrencyCell = (cell) => {
  if (!cell) return;
  const currentValue = typeof cell.value === 'object' && cell.value?.result !== undefined
    ? Number(cell.value.result)
    : Number(cell.value);
  if (!Number.isNaN(currentValue)) cell.value = toCurrencyNumber(currentValue);
  cell.numFmt = '$#,##0.00';
};

const applyCurrencyToRow = (row, columnIndexes) => {
  if (!row || !Array.isArray(columnIndexes)) return;
  columnIndexes.forEach(index => {
    const cell = row.getCell(index);
    formatCurrencyCell(cell);
  });
};

const applyTitleRowStyle = (row) => {
  if (!row) return;
  row.height = 28;
  row.eachCell(cell => {
    const existingFont = cell.font || {};
    cell.font = { ...existingFont, bold: true, size: 16, color: { argb: 'FF0F172A' } };
    const existingAlignment = cell.alignment || {};
    cell.alignment = { ...existingAlignment, horizontal: 'center', vertical: 'middle' };
  });
};

const parseDateForTitle = (dateValue) => {
  if (!dateValue) return new Date();
  if (dateValue instanceof Date) return dateValue;
  if (typeof dateValue === 'number') return new Date(dateValue);
  if (typeof dateValue === 'string') {
    if (/^\d+$/.test(dateValue)) return new Date(Number(dateValue));
    if (dateValue.includes('T')) return new Date(dateValue);
    return new Date(`${dateValue}T00:00:00`);
  }
  return new Date(dateValue);
};

const formatDateForTitle = (dateValue) => {
  try {
    return format(parseDateForTitle(dateValue), 'MMM dd, yyyy');
  } catch (error) {
    return format(new Date(), 'MMM dd, yyyy');
  }
};

const buildReportTitleWithDateRange = (title, startDate, endDate) => {
  const formattedRange = `${formatDateForTitle(startDate)} - ${formatDateForTitle(endDate)}`;
  if (title && title.trim().length > 0) return `${title.trim()} (${formattedRange})`;
  return formattedRange;
};

/**
 * Converts report data to Excel format and downloads it
 */
export const exportReportToExcel = async (reportData, reportType, roleFilter, startDate, endDate, isAdvancedReport = false, selectedAreas = null, reportTitle = '') => {
  const filename = generateFileName(reportType, roleFilter, startDate, endDate, isAdvancedReport);
  const reportTitleWithRange = buildReportTitleWithDateRange(reportTitle, startDate, endDate);
  
  // Create a new workbook
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Sweetgrass Properties';
  workbook.created = new Date();
  
  if (isAdvancedReport) {
    // For service-based reports, export flattened job data
    await generateServiceReportExcel(workbook, reportData, reportType, roleFilter, startDate, endDate, selectedAreas, reportTitleWithRange);
  } else {
    // For traditional payroll reports, export team member summary data
    await generatePayrollReportExcel(workbook, reportData, reportType, roleFilter, startDate, endDate, selectedAreas, reportTitleWithRange);
  }
  
  // Generate buffer and download
  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcel(buffer, filename);
};

/**
 * Generates Excel content for traditional payroll reports (team member summaries)
 */
const generatePayrollReportExcel = async (workbook, reportData, reportType, roleFilter, startDate, endDate, selectedAreas = null, reportTitle = '') => {
  const isHousekeeping = reportType === 'HOUSEKEEPING';
  const worksheet = workbook.addWorksheet('Payroll Report');
  
  // Add headers
  const headers = ['Team Member'];
  
  if (isHousekeeping) {
    headers.push('Property', 'Service Type', 'Scheduled Date', 'Notes', 'Flags', 'Payout', 'Billed');
  }
  
  if (reportTitle) {
    const titleRow = worksheet.addRow([reportTitle]);
    worksheet.mergeCells(titleRow.number, 1, titleRow.number, headers.length);
    applyTitleRowStyle(titleRow);
  }

  // Add header row
  const headerRow = worksheet.addRow(headers);
  applyHeaderRowStyle(headerRow);
  
  // Set column widths and text wrapping
  worksheet.columns = [
    { width: 40, style: { alignment: { wrapText: true } } }, // Column 1 - Team Member
    { width: 33, style: { alignment: { wrapText: true } } }, // Column 2 - Property
    { width: 15 }, // Column 3 - Service Type
    { width: 15 }, // Column 4 - Scheduled Date
    { width: 45, style: { alignment: { wrapText: true } } }, // Column 5 - Notes
    { width: 15 }, // Column 6 - Flags
    { width: 15 }, // Column 7 - Payout
    { width: 15 }  // Column 8 - Billed
  ];
  
  // Add area filter information if present
  if (selectedAreas && selectedAreas.length > 0) {
    worksheet.addRow([]); // Empty row
    const areaFilterText = `Filtered by areas: ${selectedAreas.map(area => area.replace(/_/g, ' ')).join(', ')}`;
    worksheet.addRow([areaFilterText]);
    worksheet.addRow([]); // Empty row
  }
  
  const hourlyEmployeesForExport = (reportData.hourlyEmployeeHours || [])
    .filter(employee => {
      if (!employee?.hours) return false;
      const hoursValue = parseFloat(employee.hours);
      return !Number.isNaN(hoursValue) && hoursValue > 0;
    })
    .map(employee => {
      const hoursValue = parseFloat(employee.hours);
      const hourlyRateValue = toCurrencyNumber(employee.hourlyRate);
      return {
        name: employee.name || 'Hourly Employee',
        job: employee.job || 'Hourly',
        hours: hoursValue,
        hourlyRate: hourlyRateValue,
        payout: toCurrencyNumber(hoursValue * hourlyRateValue),
        isContractor: employee.isContractor || false
      };
    });
  const hourlyEmployeesTotalPayout = toCurrencyNumber(hourlyEmployeesForExport.reduce((sum, employee) => sum + employee.payout, 0));
  const salariedTotalPayout = (reportData.teamMembers || []).reduce((sum, member) => sum + (Number(member.totalPayout) || 0), 0);
  const salariedTotalBilled = (reportData.teamMembers || []).reduce((sum, member) => sum + (Number(member.totalBilledAmount) || 0), 0);
  const calculatedTotalPayout = toCurrencyNumber(salariedTotalPayout + hourlyEmployeesTotalPayout);
  const calculatedTotalBilled = toCurrencyNumber(salariedTotalBilled);
  const grandTotalPayout = reportData.grandTotalPayout != null
    ? toCurrencyNumber(reportData.grandTotalPayout)
    : calculatedTotalPayout;
  const grandTotalBilledAmount = reportData.grandTotalBilledAmount != null
    ? toCurrencyNumber(reportData.grandTotalBilledAmount)
    : calculatedTotalBilled;
  
  // Add team member data
  reportData.teamMembers.forEach(teamMember => {
    if (isHousekeeping) {
      // For housekeeping, add detailed job rows
      teamMember.jobs.forEach((jobData, index) => {
        const job = jobData.job;
        const payoutValue = toCurrencyNumber(jobData.calculatedPayout || job?.payout || 0);
        const billedValue = toCurrencyNumber(jobData.calculatedBilledAmount || job?.billedAmount || 0);
        const rowValues = [
          index === 0 ? formatTeamMemberName(teamMember) : '', // Only show team member name on first row
          job.property?.name || '',
          job.serviceType?.title || '',
          formatDate(job.scheduledDate),
          job.notes || '',
          generateFlagsText(job),
          payoutValue,
          billedValue
        ];
        const jobRow = worksheet.addRow(rowValues);
        applyCurrencyToRow(jobRow, [7, 8]);
      });
      
      // Add employee totals row
      if (teamMember.jobs.length > 0) {
        const employeeTotalPayout = teamMember.usesSalaryCalculation && teamMember.totalPayout != null
          ? teamMember.totalPayout
          : teamMember.jobs.reduce((sum, jobData) => {
              const payout = jobData.calculatedPayout || jobData.job.payout || 0;
              return sum + (Number(payout) || 0);
            }, 0);
        const employeeTotalPayoutFormatted = toCurrencyNumber(employeeTotalPayout);
        const employeeTotalBilled = teamMember.jobs.reduce((sum, jobData) => {
          const billed = jobData.calculatedBilledAmount || jobData.job.billedAmount || 0;
          return sum + (Number(billed) || 0);
        }, 0);

        const employeeTotalsRow = worksheet.addRow([
          `TOTAL - ${formatTeamMemberName(teamMember)}`,
          '',
          '',
          '',
          '',
          '',
          employeeTotalPayoutFormatted,
          toCurrencyNumber(employeeTotalBilled)
        ]);
        applyCurrencyToRow(employeeTotalsRow, [7, 8]);

        applyTotalsRowStyle(employeeTotalsRow);
        
        // Add empty row between team members
        worksheet.addRow([]);
      }
    } else {
      // For non-housekeeping reports, just summary data
      worksheet.addRow([formatTeamMemberName(teamMember)]);
    }
  });
  
  if (isHousekeeping && hourlyEmployeesForExport.length > 0) {
    worksheet.addRow([]);
    const hourlyLabelRow = worksheet.addRow(['Hourly Employees']);
    applyHeaderRowStyle(hourlyLabelRow);
    const hourlyHeaderRow = worksheet.addRow(['Team Member', 'Property', 'Service Type', 'Scheduled Date', 'Notes', 'Flags', 'Payout', 'Billed']);
    applyHeaderRowStyle(hourlyHeaderRow);

    hourlyEmployeesForExport.forEach(hourlyEmployee => {
      const rateText = hourlyEmployee.hourlyRate > 0 ? ` @ $${hourlyEmployee.hourlyRate.toFixed(2)}` : '';
      const contractorLabel = hourlyEmployee.isContractor ? ' (Contractor)' : '';
      const hourlyRow = worksheet.addRow([
        `${hourlyEmployee.name}${contractorLabel}`,
        '',
        hourlyEmployee.job,
        '',
        `Hours: ${hourlyEmployee.hours}${rateText}`,
        '',
        hourlyEmployee.payout,
        0
      ]);
      applyCurrencyToRow(hourlyRow, [7, 8]);
    });

    const hourlyTotalsRow = worksheet.addRow([
      'TOTAL - Hourly Employees',
      '',
      '',
      '',
      '',
      '',
      hourlyEmployeesTotalPayout,
      0
    ]);
    applyCurrencyToRow(hourlyTotalsRow, [7, 8]);
    applyTotalsRowStyle(hourlyTotalsRow);

    worksheet.addRow([]);
  }
  
  // Add summary totals
  worksheet.addRow([]);
  const totalsRowValues = isHousekeeping
    ? [
        'TOTALS',
        '',
        '',
        '',
        '',
        '',
        grandTotalPayout,
        grandTotalBilledAmount
      ]
    : [
        'TOTALS',
        grandTotalBilledAmount
      ];
  const totalsRow = worksheet.addRow(totalsRowValues);
  applyCurrencyToRow(totalsRow, isHousekeeping ? [7, 8] : [2]);
  applyTotalsRowStyle(totalsRow);
  
  // Add employee/contractor breakdown
  const employees = reportData.teamMembers?.filter(member => 
    member.teamMemberId !== 0 && member.isContractor !== true
  ) || [];
  
  const contractors = reportData.teamMembers?.filter(member => 
    member.teamMemberId !== 0 && member.isContractor === true
  ) || [];
  
  // Employee totals (regular employees) - move outside if block for use in expenses
  const employeeTotals = employees.reduce((totals, member) => ({
    jobs: totals.jobs + (member.jobsCount || 0),
    payout: totals.payout + (Number(member.totalPayout) || 0),
    billedAmount: totals.billedAmount + (Number(member.totalBilledAmount) || 0)
  }), { jobs: 0, payout: 0, billedAmount: 0 });
  employeeTotals.payout = toCurrencyNumber(employeeTotals.payout);
  employeeTotals.billedAmount = toCurrencyNumber(employeeTotals.billedAmount);
  
  // Add hourly employees labor to employee totals (excluding contractors)
  const hourlyEmployeeLabor = (reportData.hourlyEmployeeHours || []).reduce((sum, empHours) => {
    if (!empHours.hours || empHours.hours <= 0) return sum;
    // Exclude hourly contractors from employee tax calculation
    if (empHours.isContractor === true) return sum;
    const hours = parseFloat(empHours.hours);
    const rate = empHours.hourlyRate || 0;
    return sum + (rate * hours);
  }, 0);
  const hourlyEmployeeLaborFormatted = toCurrencyNumber(hourlyEmployeeLabor);
  employeeTotals.payout = toCurrencyNumber(employeeTotals.payout + hourlyEmployeeLaborFormatted);
  
  // Calculate hourly contractor labor (contractors checked off in hourly employees settings)
  const hourlyContractorLabor = (reportData.hourlyEmployeeHours || []).reduce((sum, empHours) => {
    if (!empHours.hours || empHours.hours <= 0) return sum;
    // Only include hourly contractors
    if (empHours.isContractor !== true) return sum;
    const hours = parseFloat(empHours.hours);
    const rate = empHours.hourlyRate || 0;
    return sum + (rate * hours);
  }, 0);
  const hourlyContractorLaborFormatted = toCurrencyNumber(hourlyContractorLabor);
  
  if (employees.length > 0 || contractors.length > 0) {
    worksheet.addRow([]);
    const employeeSummaryHeaderRow = worksheet.addRow(['EMPLOYEE SUMMARY', '']);
    applyHeaderRowStyle(employeeSummaryHeaderRow);
    
    const employeesSummaryRow = worksheet.addRow([`Employees (${employees.length})`, employeeTotals.billedAmount]);
    applyCurrencyToRow(employeesSummaryRow, [2]);
    
    // Contractor totals (includes hourly contractors)
    const contractorTotals = contractors.reduce((totals, member) => ({
      jobs: totals.jobs + (member.jobsCount || 0),
      payout: totals.payout + (Number(member.totalPayout) || 0),
      billedAmount: totals.billedAmount + (Number(member.totalBilledAmount) || 0)
    }), { jobs: 0, payout: 0, billedAmount: 0 });
    contractorTotals.payout = toCurrencyNumber(contractorTotals.payout + hourlyContractorLaborFormatted);
    contractorTotals.billedAmount = toCurrencyNumber(contractorTotals.billedAmount);
    
    const contractorsSummaryRow = worksheet.addRow([`Contractors (${contractors.length})`, contractorTotals.billedAmount]);
    applyCurrencyToRow(contractorsSummaryRow, [2]);
    
    // Add financial summary
    worksheet.addRow([]);
    const financialSummaryHeaderRow = worksheet.addRow(['FINANCIAL SUMMARY', '']);
    applyHeaderRowStyle(financialSummaryHeaderRow);
    
    const totalBilled = grandTotalBilledAmount;
    const employeeLabor = toCurrencyNumber(employeeTotals.payout);
    const contractorLabor = toCurrencyNumber(contractorTotals.payout);
    const totalExpenses = toCurrencyNumber(reportData.totalExpenses || 0);
    const netProfit = toCurrencyNumber(totalBilled - employeeLabor - contractorLabor - totalExpenses);
    const profitPercentage = totalBilled > 0 ? (netProfit / totalBilled * 100) : 0;
    
    const totalBilledRow = worksheet.addRow(['Total Billed', totalBilled]);
    applyCurrencyToRow(totalBilledRow, [2]);
    applyTotalsRowStyle(totalBilledRow);
    const employeeLaborRow = worksheet.addRow(['Employee Labor', employeeLabor]);
    applyCurrencyToRow(employeeLaborRow, [2]);
    applyTotalsRowStyle(employeeLaborRow);
    const contractorLaborRow = worksheet.addRow(['Contractor Labor', contractorLabor]);
    applyCurrencyToRow(contractorLaborRow, [2]);
    applyTotalsRowStyle(contractorLaborRow);
    const expensesRow = worksheet.addRow(['Expenses', totalExpenses]);
    applyCurrencyToRow(expensesRow, [2]);
    applyTotalsRowStyle(expensesRow);
    const netProfitRow = worksheet.addRow(['Net Profit', `${formatCurrencyDisplay(netProfit)} (${profitPercentage >= 0 ? '+' : ''}${profitPercentage.toFixed(1)}%)`]);
    applyTotalsRowStyle(netProfitRow);
  }
  
  // Calculate employee tax for Excel export
  const employeeLaborForExcel = toCurrencyNumber(employeeTotals.payout); // Already includes hourly employees from above
  const employeeTaxForExcel = toCurrencyNumber(employeeLaborForExcel * 0.11);
  
  // Add expenses section if present or if there's employee tax
  const hasExpensesData = reportData.expenses && reportData.expenses.length > 0;
  
  if (hasExpensesData || employeeTaxForExcel > 0) {
    worksheet.addRow([]);
    const expensesHeaderRow = worksheet.addRow(['EXPENSES']);
    applyHeaderRowStyle(expensesHeaderRow);
    const expensesColumnsHeaderRow = worksheet.addRow(['Description', 'Date', 'Amount']);
    applyHeaderRowStyle(expensesColumnsHeaderRow);
    
    // Add manual expenses
    if (hasExpensesData) {
      reportData.expenses.forEach(expense => {
        const expenseRow = worksheet.addRow([
          expense.description || '',
          formatDate(expense.date),
          toCurrencyNumber(expense.amount || 0)
        ]);
        applyCurrencyToRow(expenseRow, [3]);
      });
    }
    
    // Add employee tax row
    if (employeeTaxForExcel > 0) {
      const employeeTaxRow = worksheet.addRow([
        'Employee Tax (11% of Employee Labor)',
        'Auto-calculated',
        employeeTaxForExcel
      ]);
      applyCurrencyToRow(employeeTaxRow, [3]);
    }
    
    const manualExpenses = hasExpensesData 
      ? reportData.expenses.reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0) 
      : 0;
    const manualExpensesFormatted = toCurrencyNumber(manualExpenses);
    const totalExpenses = toCurrencyNumber(manualExpensesFormatted + employeeTaxForExcel);
    
    worksheet.addRow([]);
    const totalExpensesRow = worksheet.addRow(['TOTAL EXPENSES', '', totalExpenses]);
    applyCurrencyToRow(totalExpensesRow, [3]);
    applyTotalsRowStyle(totalExpensesRow);
    
    // Final total
    const totalPayoutForFinal = reportData.grandTotalPayout != null ? Number(reportData.grandTotalPayout) : calculatedTotalPayout;
    const finalTotal = toCurrencyNumber(totalPayoutForFinal + totalExpenses);
    const finalTotalRow = worksheet.addRow(['FINAL TOTAL (Payout + Expenses)', '', finalTotal]);
    applyCurrencyToRow(finalTotalRow, [3]);
    applyTotalsRowStyle(finalTotalRow);
  }
};

/**
 * Generates Excel content for service-based reports (flattened job data)
 */
const generateServiceReportExcel = async (workbook, reportData, reportType, roleFilter, startDate, endDate, selectedAreas = null, reportTitle = '') => {
  const worksheet = workbook.addWorksheet('Service Report');
  
  const headers = [
    'Property Name',
    'Property Code', 
    'Area',
    'Service Type',
    'Team Member',
    'Job Status',
    'Scheduled Date',
    'Updated Date',
    'Notes',
    'Flags',
    'Payout',
    'Billed Amount'
  ];
  
  // Add header row
  if (reportTitle) {
    const titleRow = worksheet.addRow([reportTitle]);
    worksheet.mergeCells(titleRow.number, 1, titleRow.number, headers.length);
    applyTitleRowStyle(titleRow);
  }

  const headerRow = worksheet.addRow(headers);
  applyHeaderRowStyle(headerRow);
  
  // Set column widths and text wrapping
  worksheet.columns = [
    { width: 40, style: { alignment: { wrapText: true } } }, // Column 1 - Property Name
    { width: 33, style: { alignment: { wrapText: true } } }, // Column 2 - Property Code
    { width: 15 }, // Column 3 - Area
    { width: 15 }, // Column 4 - Service Type
    { width: 45, style: { alignment: { wrapText: true } } }, // Column 5 - Team Member
    { width: 15 }, // Column 6 - Job Status
    { width: 15 }, // Column 7 - Scheduled Date
    { width: 15 }, // Column 8 - Updated Date
    { width: 45, style: { alignment: { wrapText: true } } }, // Column 9 - Notes (moved to match layout)
    { width: 15 }, // Column 10 - Flags
    { width: 15 }, // Column 11 - Payout
    { width: 15 }  // Column 12 - Billed Amount
  ];
  
  // Add area filter information if present
  if (selectedAreas && selectedAreas.length > 0) {
    worksheet.addRow([]); // Empty row
    const areaFilterText = `Filtered by areas: ${selectedAreas.map(area => area.replace(/_/g, ' ')).join(', ')}`;
    worksheet.addRow([areaFilterText]);
    worksheet.addRow([]); // Empty row
  }
  
  // Flatten all jobs from all team members
  reportData.teamMembers.forEach(teamMember => {
    teamMember.jobs.forEach(jobData => {
      const job = jobData.job;
      
      // Only show payout amounts for completed jobs
      const payoutAmount = job.status === 'COMPLETED' ? toCurrencyNumber(jobData.calculatedPayout) : 0;
      const billedAmount = job.status === 'COMPLETED' ? toCurrencyNumber(jobData.calculatedBilledAmount) : 0;
      
      const flagsText = generateFlagsText(job) || 'None';
      
      const jobRow = worksheet.addRow([
        job.property?.name || '',
        job.property?.propertyCode || '',
        job.property?.area || '',
        job.serviceType?.title || '',
        formatTeamMemberName(teamMember),
        job.status || '',
        formatDate(job.scheduledDate),
        formatDate(job.updatedAt),
        job.notes || '',
        flagsText,
        payoutAmount,
        billedAmount
      ]);
      applyCurrencyToRow(jobRow, [11, 12]);
    });
  });
  
  // Calculate totals - only include completed jobs for payout totals
  const completedJobs = [];
  reportData.teamMembers.forEach(teamMember => {
    teamMember.jobs.forEach(jobData => {
      if (jobData.job.status === 'COMPLETED') {
        completedJobs.push(jobData);
      }
    });
  });
  
  const totalCompletedPayout = toCurrencyNumber(completedJobs.reduce((sum, jobData) => sum + (jobData.calculatedPayout || 0), 0));
  const totalCompletedBilledAmount = toCurrencyNumber(completedJobs.reduce((sum, jobData) => sum + (jobData.calculatedBilledAmount || 0), 0));
  
  // Add summary row
  worksheet.addRow([]);
  const summaryHeaderRow = worksheet.addRow([
    'SUMMARY', 
    '', 
    '', 
    '', 
    '', 
    '', 
    '', 
    '', 
    '', 
    `Total Jobs: ${reportData.totalJobs} (${completedJobs.length} completed)`, 
    `Total Billed: ${formatCurrencyDisplay(totalCompletedBilledAmount)}`
  ]);
  applyHeaderRowStyle(summaryHeaderRow);
  
  // Add employee/contractor breakdown for service reports
  const employees = reportData.teamMembers?.filter(member => 
    member.teamMemberId !== 0 && member.isContractor !== true
  ) || [];
  
  const contractors = reportData.teamMembers?.filter(member => 
    member.teamMemberId !== 0 && member.isContractor === true
  ) || [];
  
  // Calculate employee job totals - move outside if block for use in expenses section
  const employeeJobs = [];
  employees.forEach(member => {
    member.jobs.forEach(jobData => {
      if (jobData.job.status === 'COMPLETED') {
        employeeJobs.push(jobData);
      }
    });
  });
  
  let employeePayout = toCurrencyNumber(employeeJobs.reduce((sum, jobData) => sum + (jobData.calculatedPayout || 0), 0));
  const employeeBilled = toCurrencyNumber(employeeJobs.reduce((sum, jobData) => sum + (jobData.calculatedBilledAmount || 0), 0));
  
  // Add hourly employees labor to employee payout for service reports (excluding contractors)
  const hourlyEmployeeLaborForServiceExcel = (reportData.hourlyEmployeeHours || []).reduce((sum, empHours) => {
    if (!empHours.hours || empHours.hours <= 0) return sum;
    // Exclude hourly contractors from employee tax calculation
    if (empHours.isContractor === true) return sum;
    const hours = parseFloat(empHours.hours);
    const rate = empHours.hourlyRate || 0;
    return sum + (rate * hours);
  }, 0);
  
  employeePayout = toCurrencyNumber(employeePayout + toCurrencyNumber(hourlyEmployeeLaborForServiceExcel));
  
  // Calculate hourly contractor labor for service reports (contractors checked off in hourly employees settings)
  const hourlyContractorLaborForServiceExcel = (reportData.hourlyEmployeeHours || []).reduce((sum, empHours) => {
    if (!empHours.hours || empHours.hours <= 0) return sum;
    // Only include hourly contractors
    if (empHours.isContractor !== true) return sum;
    const hours = parseFloat(empHours.hours);
    const rate = empHours.hourlyRate || 0;
    return sum + (rate * hours);
  }, 0);
  
  if (employees.length > 0 || contractors.length > 0) {
    worksheet.addRow([]);
    const employeeSummaryHeaderRow = worksheet.addRow(['EMPLOYEE SUMMARY']);
    applyHeaderRowStyle(employeeSummaryHeaderRow);
    
    worksheet.addRow([
      `Employees (${employees.length})`, 
      '', 
      '', 
      '', 
      '', 
      '', 
      '', 
      '', 
      '', 
      `Jobs: ${employees.reduce((sum, m) => sum + m.jobsCount, 0)} (${employeeJobs.length} completed)`, 
      `Billed: ${formatCurrencyDisplay(employeeBilled)}`
    ]);
    
    // Calculate contractor job totals (includes hourly contractors)
    const contractorJobs = [];
    contractors.forEach(member => {
      member.jobs.forEach(jobData => {
        if (jobData.job.status === 'COMPLETED') {
          contractorJobs.push(jobData);
        }
      });
    });
    
    const contractorPayout = toCurrencyNumber(contractorJobs.reduce((sum, jobData) => sum + (jobData.calculatedPayout || 0), 0) + toCurrencyNumber(hourlyContractorLaborForServiceExcel));
    const contractorBilled = toCurrencyNumber(contractorJobs.reduce((sum, jobData) => sum + (jobData.calculatedBilledAmount || 0), 0));
    
    worksheet.addRow([
      `Contractors (${contractors.length})`, 
      '', 
      '', 
      '', 
      '', 
      '', 
      '', 
      '', 
      '', 
      `Jobs: ${contractors.reduce((sum, m) => sum + m.jobsCount, 0)} (${contractorJobs.length} completed)`, 
      `Billed: ${formatCurrencyDisplay(contractorBilled)}`
    ]);
    
    // Add financial summary for service reports
    worksheet.addRow([]);
    const financialSummaryHeaderRow = worksheet.addRow(['FINANCIAL SUMMARY']);
    applyHeaderRowStyle(financialSummaryHeaderRow);
    
    const totalExpensesAmount = toCurrencyNumber(reportData.totalExpenses || 0);
    const netProfit = toCurrencyNumber(totalCompletedBilledAmount - employeePayout - contractorPayout - totalExpensesAmount);
    const profitPercentage = totalCompletedBilledAmount > 0 ? (netProfit / totalCompletedBilledAmount * 100) : 0;
    
    const serviceTotalBilledRow = worksheet.addRow(['Total Billed', '', '', '', '', '', '', '', '', '', '', totalCompletedBilledAmount]);
    applyTotalsRowStyle(serviceTotalBilledRow);
    applyCurrencyToRow(serviceTotalBilledRow, [12]);
    const serviceEmployeeLaborRow = worksheet.addRow(['Employee Labor', '', '', '', '', '', '', '', '', '', '', employeePayout]);
    applyTotalsRowStyle(serviceEmployeeLaborRow);
    applyCurrencyToRow(serviceEmployeeLaborRow, [12]);
    const serviceContractorLaborRow = worksheet.addRow(['Contractor Labor', '', '', '', '', '', '', '', '', '', '', contractorPayout]);
    applyTotalsRowStyle(serviceContractorLaborRow);
    applyCurrencyToRow(serviceContractorLaborRow, [12]);
    const serviceExpensesRow = worksheet.addRow(['Expenses', '', '', '', '', '', '', '', '', '', '', totalExpensesAmount]);
    applyTotalsRowStyle(serviceExpensesRow);
    applyCurrencyToRow(serviceExpensesRow, [12]);
    const serviceNetProfitRow = worksheet.addRow(['Net Profit', '', '', '', '', '', '', '', '', '', '', `${formatCurrencyDisplay(netProfit)} (${profitPercentage >= 0 ? '+' : ''}${profitPercentage.toFixed(1)}%)`]);
    applyTotalsRowStyle(serviceNetProfitRow);
  }
  
  // Calculate employee tax for service report Excel
  const employeeLaborForServiceExcel = toCurrencyNumber(employeePayout); // Already includes hourly employees from above
  const employeeTaxForServiceExcel = toCurrencyNumber(employeeLaborForServiceExcel * 0.11);
  
  // Add expenses section if present or if there's employee tax
  const hasExpensesDataService = reportData.expenses && reportData.expenses.length > 0;
  
  if (hasExpensesDataService || employeeTaxForServiceExcel > 0) {
    worksheet.addRow([]);
    const expensesHeaderRow = worksheet.addRow(['EXPENSES']);
    applyHeaderRowStyle(expensesHeaderRow);
    const expensesColumnsHeaderRow = worksheet.addRow(['Description', 'Date', 'Amount']);
    applyHeaderRowStyle(expensesColumnsHeaderRow);
    
    // Add manual expenses
    if (hasExpensesDataService) {
      reportData.expenses.forEach(expense => {
        const expenseRow = worksheet.addRow([
          expense.description || '',
          formatDate(expense.date),
          toCurrencyNumber(expense.amount || 0)
        ]);
        applyCurrencyToRow(expenseRow, [3]);
      });
    }
    
    // Add employee tax row
    if (employeeTaxForServiceExcel > 0) {
      const employeeTaxRow = worksheet.addRow([
        'Employee Tax (11% of Employee Labor)',
        'Auto-calculated',
        employeeTaxForServiceExcel
      ]);
      applyCurrencyToRow(employeeTaxRow, [3]);
    }
    
    const manualExpensesService = hasExpensesDataService 
      ? reportData.expenses.reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0) 
      : 0;
    const manualExpensesServiceFormatted = toCurrencyNumber(manualExpensesService);
    const totalExpenses = toCurrencyNumber(manualExpensesServiceFormatted + employeeTaxForServiceExcel);
    
    worksheet.addRow([]);
    const serviceTotalExpensesRow = worksheet.addRow(['TOTAL EXPENSES', '', totalExpenses]);
    applyCurrencyToRow(serviceTotalExpensesRow, [3]);
    applyTotalsRowStyle(serviceTotalExpensesRow);
    
    // Final total
    const finalTotal = toCurrencyNumber(totalCompletedPayout + totalExpenses);
    const serviceFinalTotalRow = worksheet.addRow(['FINAL TOTAL (Payout + Expenses)', '', finalTotal]);
    applyCurrencyToRow(serviceFinalTotalRow, [3]);
    applyTotalsRowStyle(serviceFinalTotalRow);
  }
};

/**
 * Downloads Excel file using the same pattern as CSV download
 */
const downloadExcel = (buffer, filename) => {
  const blob = new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    URL.revokeObjectURL(url);
  }
};

/**
 * Generates appropriate filename for the Excel export
 */
const generateFileName = (reportType, roleFilter, startDate, endDate, isAdvancedReport) => {
  // Handle dates that might be timestamps or date strings
  const parseDate = (dateValue) => {
    if (!dateValue) return new Date();
    
    // If it's already a Date object
    if (dateValue instanceof Date) return dateValue;
    
    // If it's a number (timestamp)
    if (typeof dateValue === 'number') return new Date(dateValue);
    
    // If it's a string that looks like a date
    if (typeof dateValue === 'string') {
      // If it already has time info, use as is
      if (dateValue.includes('T')) return new Date(dateValue);
      // Otherwise add time
      return new Date(dateValue + 'T00:00:00');
    }
    
    return new Date(dateValue);
  };
  
  const startFormatted = format(parseDate(startDate), 'yyyy-MM-dd');
  const endFormatted = format(parseDate(endDate), 'yyyy-MM-dd');
  
  let filename;
  
  if (isAdvancedReport) {
    // For service-based reports, use "service-report" as the filename
    filename = 'service-report';
  } else {
    // For traditional payroll reports, use the report type
    const reportTypeFormatted = reportType.replace('_', '-').toLowerCase();
    filename = `${reportTypeFormatted}-report`;
    
    // Add role filter for traditional reports if specified
    if (roleFilter && roleFilter !== 'ALL') {
      filename += `-${roleFilter.toLowerCase().replace('_', '-')}`;
    }
  }
  
  filename += `-${startFormatted}-to-${endFormatted}.xlsx`;
  
  return filename;
};

/**
 * Formats date for Excel
 */
const formatDate = (dateValue) => {
  if (!dateValue) return '';
  
  // If it's already a Date object
  if (dateValue instanceof Date) return format(dateValue, 'yyyy-MM-dd');
  
  // If it's a number (timestamp)
  if (typeof dateValue === 'number') return format(new Date(dateValue), 'yyyy-MM-dd');
  
  // If it's a string
  if (typeof dateValue === 'string') {
    // If it already has time info, use as is
    if (dateValue.includes('T')) return format(new Date(dateValue), 'yyyy-MM-dd');
    // Otherwise add time to avoid timezone issues
    if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return format(new Date(dateValue + 'T00:00:00'), 'yyyy-MM-dd');
    }
  }
  
  return format(new Date(dateValue), 'yyyy-MM-dd');
};

